/**
 * Schedule Release Service
 * API integration for the 3-step schedule release wizard
 *
 * @version 2025.1.0
 * @description Servico de integracao com API de liberacao de agenda
 */
import apiClient from './apiClient';
import {
  ScheduleReleasePreviewRequest,
  ScheduleReleasePreviewResponse,
  ScheduleReleaseSummaryRequest,
  ScheduleReleaseSummaryResponse,
  ScheduleReleaseConfirmRequest,
  ScheduleReleaseConfirmResponse,
  ScheduleReleaseHistoryFilters,
  ScheduleReleaseHistoryResponse,
  ScheduleRelease,
  ScheduleReleaseSummaryStats,
} from '../types/scheduleRelease';

// Re-export types for convenience
export * from '../types/scheduleRelease';

class ScheduleReleaseService {
  private baseUrl = '/schedule-releases';

  /**
   * Step 1: Preview - Get holidays and schedule info for the period
   */
  async preview(data: ScheduleReleasePreviewRequest): Promise<ScheduleReleasePreviewResponse> {
    const response = await apiClient.post(`${this.baseUrl}/preview`, data);
    // Backend returns { message, data: {...} }, extract the inner data
    const responseData = response.data as { data?: ScheduleReleasePreviewResponse } | ScheduleReleasePreviewResponse;
    return (responseData as { data?: ScheduleReleasePreviewResponse }).data || responseData as ScheduleReleasePreviewResponse;
  }

  /**
   * Step 2: Summary - Calculate totals based on holiday decisions
   */
  async summary(data: ScheduleReleaseSummaryRequest): Promise<ScheduleReleaseSummaryResponse> {
    const response = await apiClient.post(`${this.baseUrl}/summary`, data);
    // Backend returns { message, data: {...} }, extract the inner data
    const responseData = response.data as { data?: ScheduleReleaseSummaryResponse } | ScheduleReleaseSummaryResponse;
    return (responseData as { data?: ScheduleReleaseSummaryResponse }).data || responseData as ScheduleReleaseSummaryResponse;
  }

  /**
   * Step 3: Confirm - Create slots and record the release
   */
  async confirm(data: ScheduleReleaseConfirmRequest): Promise<ScheduleReleaseConfirmResponse> {
    const response = await apiClient.post(`${this.baseUrl}/confirm`, data);
    // Backend returns { message, data: { release, slotsCreated } }, extract the inner data
    const responseData = response.data as { data?: { release: ScheduleRelease; slotsCreated: number }; message?: string } | ScheduleReleaseConfirmResponse;
    const innerData = (responseData as { data?: { release: ScheduleRelease; slotsCreated: number }; message?: string }).data;
    if (innerData) {
      return {
        message: (responseData as { message?: string }).message || 'Agenda liberada com sucesso',
        release: innerData.release,
        slotsCreated: innerData.slotsCreated,
      };
    }
    return responseData as ScheduleReleaseConfirmResponse;
  }

  /**
   * Get release history with pagination
   */
  async getHistory(filters: ScheduleReleaseHistoryFilters = {}): Promise<ScheduleReleaseHistoryResponse> {
    const { page = 1, limit = 20, ...rest } = filters;

    const queryParams = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
    });

    if (rest.professionalId) queryParams.append('professionalId', rest.professionalId.toString());
    if (rest.clinicId) queryParams.append('clinicId', rest.clinicId.toString());
    if (rest.companyId) queryParams.append('companyId', rest.companyId.toString());
    if (rest.startDate) queryParams.append('startDate', rest.startDate);
    if (rest.endDate) queryParams.append('endDate', rest.endDate);

    // Backend returns { message, data: { releases: [...], pagination: {...} } } or { message, data: [...], pagination: {...} }
    const response = await apiClient.get<{ data?: ScheduleRelease[]; releases?: ScheduleRelease[]; pagination?: { total: number; page: number; limit: number; totalPages: number } }>(`${this.baseUrl}/history?${queryParams.toString()}`);

    // Handle different response structures from backend
    const responseData = response.data as { data?: ScheduleRelease[]; releases?: ScheduleRelease[]; pagination?: { total: number; page: number; limit: number; totalPages: number } } | ScheduleRelease[];
    const releases = Array.isArray(responseData) ? responseData : (responseData?.releases ?? responseData?.data ?? []);
    const pagination = Array.isArray(responseData) ? undefined : responseData?.pagination;

    return {
      releases,
      total: pagination?.total ?? releases.length,
      page: pagination?.page ?? page,
      limit: pagination?.limit ?? limit,
      totalPages: pagination?.totalPages ?? Math.ceil((pagination?.total ?? releases.length) / limit),
    };
  }

  /**
   * Get release by ID
   */
  async getById(id: number): Promise<ScheduleRelease> {
    const response = await apiClient.get(`${this.baseUrl}/${id}`);
    return response.data || response;
  }

  /**
   * Get summary statistics for a professional/clinic
   */
  async getStats(
    professionalId: number,
    clinicId: number,
    year?: number
  ): Promise<ScheduleReleaseSummaryStats> {
    const queryParams = new URLSearchParams({
      professionalId: professionalId.toString(),
      clinicId: clinicId.toString(),
    });

    if (year) {
      queryParams.append('year', year.toString());
    }

    const response = await apiClient.get(`${this.baseUrl}/stats?${queryParams.toString()}`);
    return response.data || response;
  }

  /**
   * Health check
   */
  async healthCheck(): Promise<{ status: string; module: string; timestamp: string }> {
    const response = await apiClient.get(`${this.baseUrl}/health`);
    return response.data || response;
  }
}

// Export singleton instance
export const scheduleReleaseService = new ScheduleReleaseService();
export default scheduleReleaseService;
